// Batch-rename engine for press-kit files.
//
// Pure: no React, no network, no Supabase state. That matters for more than
// testability — the modal's preview and the payload sent to Dropbox are
// produced by this same code, so what you approve cannot disagree with what
// happens.
//
// The pipeline order is FIXED rather than a user-arranged rule stack. A
// reorderable stack is a great deal of UI for a case almost nobody hits, and a
// fixed order can be printed in the modal so the result is never a surprise:
//
//   current stem (Edit) ──┐
//                         ├→ find/replace → remove → case → affixes → sequence → clean-up
//   pattern (Pattern) ────┘
//
// The extension is split off before any of that and re-appended after, so no
// transform can eat or mangle it. For the same reason there is deliberately no
// {ext} token: the extension is always preserved, and a token for it would
// only ever produce AR_01jpg.jpg.

import { BRIO_PRESS_BASE, detectCredit } from './pressAssets';

/** Dropbox is case-insensitive, so collisions must be judged case-folded. */
const fold = s => (s || '').toLowerCase();

/**
 * Splits a trailing extension off a file name. A leading dot is part of the
 * name (".gitignore" is not an extension), so the search starts past index 0.
 */
export function splitExt(name) {
  const s = name || '';
  const i = s.lastIndexOf('.');
  if (i <= 0) return { stem: s, ext: '' };
  return { stem: s.slice(0, i), ext: s.slice(i) };
}

export function dirOf(path) {
  const i = (path || '').lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

// ── THE TWO SEPARATORS ────────────────────────────────────────────────────
//
// The house convention gives "_" and "-" different jobs:
//
//   Architecture-BRIO_Ambika-River-Home_Alibaug_Fernandes_01_DSC-0143_Courtyard-At-Dusk.jpg
//   └── one field ──┘ └──── one field ───┘
//
//   _   separates FIELDS
//   -   stands in for a space INSIDE a field
//
// So a space can never become "_", and any "_" already inside a value (a camera
// name like DSC_0143) has to become "-" — otherwise the existing name would
// masquerade as two extra fields and the name would no longer parse.
//
// That is why values are cleaned as they are substituted rather than by one
// pass over the finished string: at that point the field separators the pattern
// contributed are indistinguishable from the ones inside a value.

/**
 * Title case that leaves acronyms alone.
 *
 * Plain title case would turn DSC-0143 into Dsc-0143 and BRIO into Brio. A word
 * that is already all-caps is deliberate, so it is left as it stands.
 */
export function toTitleCase(s) {
  return (s || '').replace(/[^\s_-]+/g, w => (
    w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w)
      ? w
      : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ));
}

/**
 * One field's worth of text: ASCII, hyphen-joined, optionally title-cased.
 * Applied to each token value as it is substituted.
 */
export function fieldValue(value, { titleCase = false } = {}) {
  let s = String(value ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // é → e, before the ASCII strip
    .replace(/[\\/:?*"<>|]/g, '')                        // rejected by Dropbox
    .replace(/[^\x20-\x7E]/g, '');                       // header-safe
  if (titleCase) s = toTitleCase(s);
  return s
    .replace(/[\s_]+/g, '-')   // spaces and any existing underscores are intra-field
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
}

/**
 * The final pass over the assembled stem, and not optional.
 *
 * Beyond the characters Dropbox rejects, non-ASCII has to go: the Dropbox API
 * takes its arguments in a `Dropbox-API-Arg` HTTP *header*, so a file named
 * with an accented character produces an invalid header rather than a useful
 * error. upload-reimbursement-dropbox guards this; export-social-post does not.
 *
 * Collapsing runs of "_" is what lets an empty field disappear cleanly rather
 * than leaving a gap — a photograph with no description just ends sooner.
 */
export function cleanName(stem) {
  return (stem || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[\\/:?*"<>|]/g, '')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/ /g, '-')        // a space is always intra-field; "_" is structural
    .replace(/-{2,}/g, '-')
    .replace(/_{2,}/g, '_')
    .replace(/^[_\-.]+|[_\-.]+$/g, '');
}

/**
 * The values a pattern can interpolate, read from the file's own position in
 * the press tree: /BRIO-press/{project}/{section}/{photographer}/file.jpg.
 * Those folder names are the only description most of these files have.
 */
export function tokenContext(entry, { kitSchema = [], n = 1, rank = 0, project = null, description = '' } = {}) {
  const path = entry?.path || '';
  const rel = path.startsWith(`${BRIO_PRESS_BASE}/`) ? path.slice(BRIO_PRESS_BASE.length + 1) : path;
  const segments = rel.split('/');
  const name = entry?.name || segments[segments.length - 1] || '';
  // Project folders are named "<CODE>_<Project Name>" (kit._syncedAs), so the
  // code is everything before the first underscore.
  const projectFolder = segments[0] || '';
  const photographer = kitSchema.length ? detectCredit(path, kitSchema) : (segments[2] || '');
  // The full name and the location come from the comms.projects row when the
  // caller has it. Neither is recoverable from the path — the folder holds an
  // abbreviated name and no location at all — so falling back to the folder is
  // a best effort, not an equivalent.
  const folderName = projectFolder.includes('_') ? projectFolder.slice(projectFolder.indexOf('_') + 1) : projectFolder;
  return {
    project: projectFolder.includes('_') ? projectFolder.split('_')[0] : projectFolder,
    projectName: project?.name || folderName,
    // City and country as one field: fieldValue collapses the space to a
    // hyphen, so "Alibaug India" lands as Alibaug-India and "New Delhi India"
    // as New-Delhi-India. Either half missing just shortens it; both missing
    // and the field drops out of the name entirely.
    location: [project?.city, project?.country].map(v => (v || '').trim()).filter(Boolean).join(' '),
    section: segments[1] || '',
    photographer,
    name: splitExt(name).stem,
    description: description || '',
    date: (entry?.clientModified || '').slice(0, 10),
    rank: rank ? String(rank) : '',
    n,
  };
}

export const TOKENS = [
  'projectName', 'location', 'photographer', 'n', 'name', 'description',
  'project', 'section', 'date', 'rank',
];

/**
 * Interpolates {token} and {n:02} (the digits set the zero-padded width).
 *
 * Values are field-cleaned as they land — spaces and stray underscores become
 * hyphens — while the literal text around them is left exactly as written. That
 * is what keeps "Architecture-BRIO" in a pattern intact while still turning a
 * project called "Ambika River Home" into "Ambika-River-Home".
 *
 * An unrecognised token is left standing rather than replaced with nothing —
 * a typo then shows up in the preview instead of silently deleting text.
 */
export function expandTokens(pattern, ctx, { titleCase = false } = {}) {
  return (pattern || '').replace(/\{(\w+)(?::0?(\d+))?\}/g, (whole, key, width) => {
    const v = ctx?.[key];
    if (v === undefined || v === null) return whole;
    // The sequence number is a number, not prose: pad it, never title-case it.
    if (width) return String(v).padStart(Number(width), '0');
    return fieldValue(v, { titleCase });
  });
}

const escapeLiteral = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ── RE-APPLYING THE PATTERN ───────────────────────────────────────────────
//
// The protocol carries EXISTING-NAME, which makes it self-consuming: run the
// pattern over a name it already produced and the whole previous name is
// folded back into the {name} slot, doubling on every pass. The intended
// workflow is exactly two passes — curate the raw names, then apply the
// protocol — so a third pass by accident is a live risk, not a theoretical one.
// Collision detection cannot catch it either, because each doubled name is
// unique.
//
// The guard: before expanding, check whether the current name is one this very
// pattern already produced for this very file. If it is, recover what was in
// the {name} and {description} slots and reuse those instead of the whole
// string. The second pass then reproduces its own output, and the pattern
// becomes safe to repeat.
//
// This is only possible because "_" is a strict field delimiter and no cleaned
// value may contain one: splitting the pattern on "_" yields exactly the field
// list, and [^_]+ matches exactly one field. It is deliberately strict — every
// other field must match the value this file would produce now — so a file
// merely resembling the protocol is left alone.

/**
 * A regex that matches names this pattern would produce for this file, with
 * {name} and {description} captured. Returns null when the pattern has neither
 * to recover, or when a field cannot be pinned down.
 */
export function buildPatternMatcher(pattern, ctx, { titleCase = true, captureN = false } = {}) {
  const pieces = [];
  const captures = [];
  let named = 0;
  for (const field of String(pattern || '').split('_')) {
    if (!field) continue;
    const token = /^\{(\w+)(?::0?(\d+))?\}$/.exec(field);
    if (!token) { pieces.push({ re: escapeLiteral(field) }); continue; }
    const key = token[1];
    if (key === 'name' || key === 'description') {
      captures.push(key);
      named++;
      // Optional for description: an empty one collapses out of the name.
      pieces.push({ re: '([^_]+)', optional: key === 'description' });
      continue;
    }
    // Any width — the padding may have been changed since these were named.
    // Captured only when a caller is reading the numbers back out; parsing a
    // prior run does not want it, and capturing unconditionally would shift
    // every other group's index.
    if (key === 'n') {
      if (captureN) captures.push('n');
      pieces.push({ re: captureN ? '(\\d+)' : '\\d+' });
      continue;
    }
    const value = fieldValue(ctx?.[key] ?? '', { titleCase });
    // An empty field left no trace in the name, so it leaves none in the regex.
    if (value) pieces.push({ re: escapeLiteral(value) });
  }
  // A pattern with nothing recoverable in it cannot identify its own output.
  // Counted on `named` rather than `captures` so that turning on captureN does
  // not quietly make a {name}-less pattern parseable.
  if ((!named && !captureN) || !pieces.length) return null;
  if (!captures.length) return null;

  const src = pieces.map((p, i) => {
    const sep = i === 0 ? '' : '_';
    return p.optional ? `(?:${sep}${p.re})?` : `${sep}${p.re}`;
  }).join('');
  return { re: new RegExp(`^${src}$`, 'i'), captures };
}

/**
 * What a previous run of this pattern put in the {name} and {description}
 * slots, or null if this name was not produced by it.
 */
export function parseByPattern(stem, pattern, ctx, opts) {
  const matcher = buildPatternMatcher(pattern, ctx, opts);
  if (!matcher) return null;
  const m = matcher.re.exec(stem || '');
  if (!m) return null;
  const out = {};
  matcher.captures.forEach((key, i) => { out[key] = m[i + 1] || ''; });
  return out;
}

/** Whether a pattern actually numbers anything. */
export const hasSequenceToken = pattern => /\{n(?::0?\d+)?\}/.test(String(pattern || ''));

/**
 * The lowest sequence number this pattern has not already used in a folder.
 *
 * Renaming a shoot rarely happens in one pass — a few get done, more arrive,
 * someone re-runs it a week later. `{n}` counts position in the current
 * selection, so every pass starts at 1 and the folder ends up with two 001s.
 * This reads the numbers already on disk so the next pass can carry on from
 * where the last one stopped.
 *
 * Only files this exact pattern produced are counted; anything else in the
 * folder has no sequence number to speak of. Returns null when the folder
 * holds none of them, because then there is nothing to suggest and starting
 * at 1 is already right.
 */
export function nextFreeSequence(names, pattern, ctx, opts = {}) {
  const matcher = buildPatternMatcher(pattern, ctx, { ...opts, captureN: true });
  const at = matcher ? matcher.captures.indexOf('n') : -1;
  if (at < 0) return null;

  let max = 0;
  (names || []).forEach(nm => {
    const m = matcher.re.exec(splitExt(nm).stem);
    if (!m) return;
    const v = parseInt(m[at + 1], 10);
    if (Number.isFinite(v) && v > max) max = v;
  });
  return max ? max + 1 : null;
}

/**
 * What to offer as the starting number for this selection, or null for nothing
 * worth suggesting.
 *
 * Across several folders it takes the highest, not each folder's own next:
 * the modal has one start field, and the high-water mark is the only value
 * that cannot land on a number already used somewhere in the batch.
 */
export function suggestSeqStart(entries, rules = {}, opts = {}) {
  if (rules.mode !== 'pattern' || !hasSequenceToken(rules.pattern)) return null;
  const { kitSchema = [], ranksById = {}, namesByFolder = {}, project = null } = opts;

  let best = null;
  const seen = new Set();
  (entries || []).forEach(entry => {
    const folder = entry.folderPath || dirOf(entry.path);
    if (seen.has(folder)) return;
    seen.add(folder);
    const ctx = tokenContext(entry, {
      kitSchema, n: 1, rank: ranksById[entry.fileId] || 0, project,
    });
    const next = nextFreeSequence(namesByFolder[folder] || [], rules.pattern, ctx, {
      titleCase: rules.titleCase !== false,
    });
    if (next && (best === null || next > best)) best = next;
  });
  return best;
}

/**
 * Compiles the find/replace rule once per batch. Returns `{ re, error }`; an
 * invalid regex is reported rather than silently ignored, so the modal can
 * block the apply instead of renaming everything to itself.
 */
export function compileFind({ find, regex = false, caseSensitive = false, replaceAll = true } = {}) {
  if (!find) return { re: null, error: null };
  const flags = `${replaceAll ? 'g' : ''}${caseSensitive ? '' : 'i'}`;
  try {
    return { re: new RegExp(regex ? find : escapeLiteral(find), flags), error: null };
  } catch (e) {
    return { re: null, error: `Invalid regular expression: ${e.message}` };
  }
}

function applyCase(stem, mode) {
  if (mode === 'upper') return stem.toUpperCase();
  if (mode === 'lower') return stem.toLowerCase();
  if (mode === 'title') return toTitleCase(stem);
  return stem;
}

/**
 * The fixed pipeline, applied to one stem. `re` comes from compileFind so the
 * regex is built once for the batch rather than once per file.
 */
export function applyRules(stem, rules = {}, ctx = {}, re = null) {
  // Title case belongs to pattern mode: it is applied per field as each value
  // lands, so the literal text of the pattern keeps the capitalisation it was
  // written with. Edit mode has its own whole-stem `caseMode` instead.
  let s = rules.mode === 'pattern'
    ? expandTokens(rules.pattern || '', ctx, { titleCase: rules.titleCase !== false })
    : stem;

  if (re) s = s.replace(re, rules.replace || '');

  const first = Number(rules.removeFirst) || 0;
  const last = Number(rules.removeLast) || 0;
  if (first > 0) s = s.slice(first);
  if (last > 0) s = s.slice(0, Math.max(0, s.length - last));

  s = applyCase(s, rules.caseMode);

  if (rules.prefix) s = rules.prefix + s;
  if (rules.suffix) s = s + rules.suffix;

  if (rules.sequence) {
    const pad = Number(rules.seqPad) || 1;
    const num = String(ctx.n ?? 1).padStart(pad, '0');
    const sep = rules.seqSep ?? '_';
    s = rules.seqPosition === 'prefix' ? `${num}${sep}${s}` : `${s}${sep}${num}`;
  }

  return cleanName(s);
}

/**
 * Turns a selection plus a rule set into the exact list of renames to perform,
 * with every row's fate decided up front.
 *
 * Statuses:
 *   ok         — will be renamed
 *   unchanged  — the rules are a no-op for this file; skipped, not an error
 *   invalid    — the rules produce an empty or unusable name
 *   collision  — two files would land on one name, or the name is already
 *                taken by a file that is NOT itself being renamed away
 *
 * A target that collides with a name being *vacated* in the same batch (a
 * swap, a rotation, or a case-only change on Dropbox's case-insensitive
 * namespace) is not an error — it is flagged `staged`, and the server renames
 * through a temporary name so the two passes cannot trip over each other.
 *
 * Sequence numbering restarts per folder, in the order given (which is the
 * order on screen), so each photographer's folder counts from 1.
 */
export function buildRenamePlan(entries, rules = {}, opts = {}) {
  const { kitSchema = [], ranksById = {}, namesByFolder = {}, project = null, descriptions = {} } = opts;
  const { re, error: findError } = compileFind(rules);

  const start = Number.isFinite(Number(rules.seqStart)) && rules.seqStart !== '' ? Number(rules.seqStart) : 1;
  const step = Number(rules.seqStep) || 1;

  // One counter per folder, so each photographer's folder counts from the
  // start value rather than continuing the one before it.
  const counters = new Map();
  const rows = (entries || []).map(entry => {
    const folder = entry.folderPath || dirOf(entry.path);
    const n = counters.has(folder) ? counters.get(folder) + step : start;
    counters.set(folder, n);

    const { stem, ext } = splitExt(entry.name || '');
    const ctx = tokenContext(entry, {
      kitSchema, n, rank: ranksById[entry.fileId] || 0, project,
    });

    // If this name is one this same pattern already produced, reuse what was in
    // the {name} and {description} slots rather than swallowing the whole
    // string — see the guard above.
    const prior = rules.mode === 'pattern'
      ? parseByPattern(stem, rules.pattern, ctx, { titleCase: rules.titleCase !== false })
      : null;
    if (prior?.name) ctx.name = prior.name;

    // A typed description wins, including an empty one — clearing the box is a
    // decision, not an absence, so it must not fall back to the recovered text.
    const typed = Object.prototype.hasOwnProperty.call(descriptions, entry.path);
    ctx.description = typed ? descriptions[entry.path] : (prior?.description || '');

    const nextStem = findError ? stem : applyRules(stem, rules, ctx, re);
    const to = `${nextStem}${ext}`;

    return {
      entry,
      folder,
      stem: nextStem,
      priorDescription: prior?.description || '',
      from: entry.name || '',
      to,
      fromPath: entry.path,
      toPath: `${folder}/${to}`,
      status: 'ok',
      note: '',
    };
  });

  // Names this batch frees up. A target landing on one of these is a swap, not
  // a clash — the server stages it through a temp name.
  const vacating = new Map();
  rows.forEach(r => {
    if (!vacating.has(r.folder)) vacating.set(r.folder, new Set());
    vacating.get(r.folder).add(fold(r.from));
  });

  const claimed = new Map(); // folder -> Map(foldedName -> row)
  rows.forEach(r => {
    if (findError) { r.status = 'invalid'; r.note = findError; return; }
    if (!r.stem) { r.status = 'invalid'; r.note = 'Produces an empty name'; return; }
    if (r.to === r.from) { r.status = 'unchanged'; return; }

    if (!claimed.has(r.folder)) claimed.set(r.folder, new Map());
    const inFolder = claimed.get(r.folder);
    const key = fold(r.to);

    const taken = inFolder.get(key);
    if (taken) {
      r.status = 'collision';
      r.note = `Same new name as ${taken.from}`;
      taken.status = 'collision';
      taken.note = `Same new name as ${r.from}`;
      return;
    }
    inFolder.set(key, r);

    const onDisk = (namesByFolder[r.folder] || []).some(nm => fold(nm) === key);
    if (onDisk && !vacating.get(r.folder)?.has(key)) {
      r.status = 'collision';
      r.note = 'A file with that name is already in this folder';
      return;
    }
    // Renaming onto a name this batch is freeing, or a case-only change on a
    // case-insensitive namespace: both need a temp name in between.
    if ((onDisk && vacating.get(r.folder).has(key)) || fold(r.from) === key) r.staged = true;
  });

  return rows;
}

/** Counts for the modal's summary line and for gating the apply button. */
export function planSummary(rows) {
  const count = s => rows.filter(r => r.status === s).length;
  return {
    total: rows.length,
    ok: count('ok'),
    unchanged: count('unchanged'),
    collision: count('collision'),
    invalid: count('invalid'),
    staged: rows.filter(r => r.staged && r.status === 'ok').length,
    blocked: count('collision') + count('invalid') > 0,
  };
}
